/**
 * Copyright (c) 2010-2021 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */

import { Inject, Injectable } from '@angular/core';
import { OpenIdConfig, OpenIdStateModel } from './openIdState.model';
import { Action, Actions, NgxsOnInit, Selector, State, StateContext, Store } from '@ngxs/store';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { AuthState } from './authState.state';
import { LoggerService } from '../../../modules/shared/services/logger.service';
import { APP_BASE_HREF } from '@angular/common';
import { concatMap, filter, map, take } from 'rxjs/operators';
import { AuthStateModel } from './authState.model';
import { LoginSuccessful, SetRequestedRoute } from './authState.actions';
import { ConfigureOpenId, OpenIdLoginSuccessful } from './openIdState.actions';
import { patch } from '@ngxs/store/operators';
import { DashGisUtil } from '../../../modules/shared/dash-gis-util';
import { from, timer } from 'rxjs';

@State<OpenIdStateModel>({
    name: 'openid',
    defaults: {
        config: {},
        buttonText: 'Login with OpenID',
        initialized: false,
    },
})
@Injectable()
export class OpenIdState implements NgxsOnInit {
    constructor(
        @Inject(APP_BASE_HREF) private baseHref,
        private logger: LoggerService,
        private oauthService: OAuthService,
        private store: Store,
        private actions$: Actions
    ) {}

    ngxsOnInit(ctx?: StateContext<OpenIdStateModel>): void {
        // load the discovery document
        this.store
            // are we using OIDC at all?
            .select(OpenIdState.shouldInitOpenIdConnect)
            .pipe(
                filter((b) => b),
                // get the OIDC configuration (auth/config method  + defaults)
                concatMap(() => this.store.select(AuthState.methodConfig).pipe(map((fn) => fn('oidc')))),
                take(1)
            )
            .subscribe((openIdConfig: OpenIdConfig) => {
                const oidcConfig = this.getOpenIdConnectConfig(openIdConfig.oidcConfig);
                ctx.dispatch(
                    new ConfigureOpenId({
                        oidcConfig,
                        buttonText: openIdConfig.buttonText,
                    })
                );
            });

        timer(1000, 10000)
            .pipe(
                filter(() => ctx.getState().initialized),
                filter(() => this.oauthService.hasValidIdToken()),
                filter(() => !!this.oauthService.getRefreshToken()),
                filter(() => {
                    const exp = this.oauthService.getAccessTokenExpiration();
                    const now = new Date().getTime();
                    return exp - now < 1000 * 60 * 45; // check when within 5 minutes of exp
                }),
                concatMap(() => from(this.oauthService.refreshToken()))
            )
            .subscribe();
    }

    @Action(OpenIdLoginSuccessful)
    public openIdLoginSuccessful({ dispatch }: StateContext<OpenIdStateModel>) {
        let stateUrl = this.oauthService.state;
        if (!stateUrl) {
            return;
        }
        if (!stateUrl.startsWith('/')) {
            stateUrl = decodeURIComponent(stateUrl);
            const requestedRoute = DashGisUtil.buildRequestedRoute(stateUrl);
            dispatch(new SetRequestedRoute(requestedRoute))
                .toPromise()
                .then(() =>
                    dispatch(
                        new LoginSuccessful({
                            activeMethod: 'oidc',
                            token: '',
                            refreshToken: '',
                        })
                    )
                );
        }
    }

    @Action(ConfigureOpenId)
    public configureOpenId(ctx: StateContext<OpenIdStateModel>, action: ConfigureOpenId) {
        this.oauthService.configure(action.config.oidcConfig);
        this.oauthService
            .loadDiscoveryDocument()
            .then(() => {
                ctx.setState(
                    patch({
                        config: action.config.oidcConfig,
                        buttonText: action.config.buttonText,
                        initialized: true,
                    })
                );
            })
            .catch(() => this.logger.error('Error Attempting to load discovery document'));
    }

    private getOpenIdConnectConfig(config): AuthConfig {
        return {
            issuer: config.issuer,
            clientId: config.clientId,
            redirectUri: this.baseHref.endsWith('/')
                ? `${window.location.origin}${this.baseHref}`
                : `${window.location.origin}${this.baseHref}/`,
            responseType: config.responseType || 'code',
            requireHttps: 'remoteOnly',

            // Ask offline_access to support refresh token refreshes
            scope: config.scope || 'openid profile email offline_access',

            // Needed for Code Flow to suggest using iframe-based refreshes
            useSilentRefresh: false,

            strictDiscoveryDocumentValidation: config.strictDiscoveryDocumentValidation ?? true,
            logoutUrl: config.logoutUrl || null,
            // For faster testing
            timeoutFactor: 1.0,
            sessionChecksEnabled: false,
            sessionCheckIntervall: 10000,
            // Also requires enabling "Verbose" level in devtools
            showDebugInformation: config.debug || false,
            // https://github.com/manfredsteyer/angular-oauth2-oidc/issues/457#issuecomment-431807040
            clearHashAfterLogin: false,
            // Real semicolon gets mangled by IdentityServer's URI encoding
            nonceStateSeparator: ';',
        };
    }

    @Selector([AuthState])
    static shouldInitOpenIdConnect(state: OpenIdStateModel, authState: AuthStateModel): boolean {
        return authState.methods.hasOwnProperty('oidc');
    }

    @Selector()
    static initialized(state: OpenIdStateModel) {
        return state.initialized;
    }

    @Selector()
    static buttonText(state: OpenIdStateModel) {
        return state.buttonText;
    }
}
